import React from 'react';
import type { CaptureProps, ImportAdapter, ImportedItem } from './types';
import { parseCsv } from './csv';
import { csvHeaderSet } from './detect';
import { readCsvFileTexts } from './captureFiles';

/** Parse a LastPass CSV export string into ImportedItems. */
export function parseLastPass(csv: string): ImportedItem[] {
  const rows = parseCsv(csv);
  if (rows.length < 2) return [];

  const headers = rows[0];
  const col = (name: string) => headers.indexOf(name);

  const iUrl      = col('url');
  const iUsername = col('username');
  const iPassword = col('password');
  const iTotp     = col('totp');
  const iExtra    = col('extra');
  const iName     = col('name');
  const iGrouping = col('grouping');
  const iFav      = col('fav');

  return rows.slice(1).map(cells => {
    const get = (i: number) => (i >= 0 ? (cells[i] ?? '').trim() : '');

    const item: ImportedItem = {
      type: 'login',
      name: get(iName) || get(iUrl) || 'Untitled',
      sensitive: false,
      favorite: get(iFav) === '1',
      tags: [],
      importSource: 'lastpass',
    };

    const url      = get(iUrl);
    const username = get(iUsername);
    const password = get(iPassword);
    const totp     = get(iTotp);
    const extra    = get(iExtra);
    const grouping = get(iGrouping);

    if (url)      item.url      = url;
    if (username) item.username = username;
    if (password) item.password = password;
    if (totp)     item.totp     = totp;
    if (extra)    item.content  = extra;
    if (grouping) item.group    = grouping;

    return item;
  });
}

// ─── CaptureComponent ─────────────────────────────────────────────────────────

const LASTPASS_EXPORT_STEPS = [
  'In LastPass, click your account icon in the top-right.',
  'Choose Account → Advanced → Export.',
  'Pick "LastPass CSV File" and re-enter your master password if prompted.',
  'LastPass saves a file called lastpass_export.csv to your downloads.',
];

function LastPassCapture({ onCapture, onError }: CaptureProps) {
  async function handleFiles(e: React.ChangeEvent<HTMLInputElement>) {
    const texts = await readCsvFileTexts(e.target.files, onError, 'LastPass CSV export');
    if (texts) onCapture(texts);
  }

  return (
    <div className="flex flex-col gap-4">
      <div className="bg-surface border border-divider rounded-xl p-4">
        <h3 className="text-sm font-semibold text-ink mb-2">How to export from LastPass</h3>
        <ol className="text-sm text-ink-secondary list-decimal list-inside space-y-1">
          {LASTPASS_EXPORT_STEPS.map((step, i) => <li key={i}>{step}</li>)}
        </ol>
      </div>

      <div className="flex flex-col items-center gap-4 p-6 border-2 border-dashed border-divider rounded-xl">
        <svg className="w-10 h-10 text-ink-faint" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5}
            d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
        </svg>
        <label className="cursor-pointer px-4 py-2 bg-hbs-primary hover:bg-hbs-primary-hover text-hbs-on-primary text-sm font-medium rounded-lg transition-colors">
          Choose CSV file(s)
          <input type="file" accept=".csv" multiple className="hidden" onChange={handleFiles} />
        </label>
      </div>
    </div>
  );
}

/** LastPass CSV: `url,username,password,totp,extra,name,grouping,fav`. The
 *  `grouping` + `fav` columns are unique to LastPass among the sources we read. */
function detectLastPass(rawInput: string): boolean {
  const h = csvHeaderSet(rawInput);
  return h.has('password') && h.has('grouping') && h.has('fav');
}

export const LastPassAdapter: ImportAdapter = {
  id: 'lastpass',
  name: 'LastPass',
  description: 'Import from a LastPass CSV export',
  CaptureComponent: LastPassCapture,
  parse: parseLastPass,
  detect: detectLastPass,
};
